// Upload timeline: maps the upload manager's jobs to a stage-by-stage model of one folder's pipeline,
// so the uploader can draw it as a swimlane (desktop) or a vertical timeline (phone).
//
// A preview-first folder runs as several jobs: a COPY pass (quick previews, then face & people search
// of each copy) and, once the copies have uploaded, an ORIGINALS pass (untouched files) — plus small
// prioritised originals batches for photos that sold meanwhile. They share `pairId` (the copy job's id).
// The stages, in the photographer's terms:
//
//   Previews uploading → Live on previews → Finding faces & people → Originals uploading → Complete
//
// Scanning and the originals overlap in time (the originals never wait for scanning), and scanning
// starts while the previews are still uploading — the model keeps real start/end times per stage so
// the view can draw that overlap on a shared elapsed-time axis.
//
// Pure: everything derives from job state + `now`; nothing here reads the clock or the DOM.
import type { UploadJob } from "@/hooks/useUploadManager";
import { formatOriginalsBy } from "./originalsBy";
import { mergeCounts } from "./failReasons";

/** "Upload previews now, originals later", once the previews are in: "Previews done · originals expected by Sat 18 Oct, 23:00". */
export const originalsLaterStatus = (originalsBy: number, lang?: string): string =>
  `Previews done · originals expected by ${formatOriginalsBy(originalsBy, lang)}`;

export type TimelineJob = Pick<
  UploadJob,
  | "id"
  | "galleryName"
  | "engineGuid"
  | "kind"
  | "phase"
  | "total"
  | "completed"
  | "failed"
  | "bytesTotal"
  | "bytesSent"
  | "startedAt"
  | "seq"
> &
  Partial<
    Pick<
      UploadJob,
      | "folder"
      | "finishedAt"
      | "uploadingAt"
      | "contentDoneAt"
      | "previewFirst"
      | "pairId"
      | "soldFirst"
      | "originalsBy"
      | "paused"
      | "waitingForSignIn"
      | "conc"
      | "scan"
      | "scanTotal"
      | "scanCompleted"
      | "files"
      | "failReasons"
      | "alreadyThere"
      | "retrying"
    >
  >;

export type StageKey = "previews" | "live" | "scan" | "originals" | "complete" | "upload";
export type StageState = "pending" | "waiting" | "active" | "paused" | "done" | "skipped" | "failed" | "cancelled";

/** Sold photos sent ahead of the rest: a prioritised batch ("batch"), or the sold photos placed at the
 *  front of the main originals pass ("lead" — a marker at its start). */
export interface PrioritySegment {
  kind: "batch" | "lead";
  start: number;
  end?: number;
  count: number;
  done: number;
}

export interface TimelineStage {
  key: StageKey;
  label: string;
  /** Short label for the compact stepper. */
  short: string;
  state: StageState;
  /** The state in words (never colour-only). */
  stateLabel: string;
  /** A point in time (Live / Complete) rather than a span. */
  milestone: boolean;
  done: number;
  total: number;
  failed: number;
  /** % complete by files, or null when there's nothing to count. */
  pct: number | null;
  /** Span on the time axis (epoch ms); `end` is unset while it's still going. */
  start?: number;
  end?: number;
  /** Milestone time (reached, or expected). */
  at?: number;
  /** Upload throughput (bytes/s) for upload lanes; photos/min for the scan lane. */
  rate?: number;
  rateUnit?: "bytes" | "photos";
  etaSec: number | null;
  /** "3,214/5,001 · 64%"-style one-liner. */
  summary: string;
  /** Plain-English why / what next. */
  note?: string;
  priority?: PrioritySegment[];
  /** Other stages running over the same stretch of time. */
  parallelWith?: StageKey[];
}

export interface UploadTimeline {
  id: string;
  kind: "preview-first" | "originals" | "plain";
  galleryName: string;
  engineGuid: string;
  folder?: string;
  startedAt: number;
  now: number;
  /** Right edge of the shared time axis (≥ now while running; leaves room for projected ends). */
  axisEnd: number;
  running: boolean;
  paused: boolean;
  signedOut: boolean;
  cancelled: boolean;
  complete: boolean;
  headline: string;
  /** The one thing to know right now — why something is waiting, or what's next. */
  reason?: string;
  stages: TimelineStage[];
  /** Sold photos whose originals haven't gone yet (sold-first). */
  soldWaiting: number;
  /** Sold photos sent first across this pipeline. */
  soldFirst: number;
  /** Why files failed across the pipeline (failReasons.FailKey → count). */
  failReasons: Record<string, number>;
  /** Files counted done because the server already had them (never failures). */
  alreadyThere: number;
  /** Files that didn't go through and are being tried again by themselves: how many, and in how many seconds
   *  (0 = being sent now). Absent when nothing is waiting on a retry. */
  retrying?: { count: number; inSec: number };
}

export interface TimelineOptions {
  now: number;
  /** Is the gallery published? Unknown → treated as live once the previews are up. */
  published?: boolean;
  /** An auto-publish is armed for when the previews are up. */
  autoPublishArmed?: boolean;
}

// ---- formatting ------------------------------------------------------------------------------------

export const fmtCount = (n: number) => Math.max(0, Math.round(n)).toLocaleString();

export const fmtRate = (bps?: number): string => {
  if (!bps || bps <= 0) return "";
  const mb = bps / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB/s`;
  return `${Math.max(1, Math.round(bps / 1024))} KB/s`;
};

/** Seconds → "45s" / "2m 10s" / "1h 05m"; "" when unknown. */
export const fmtDuration = (secs: number | null | undefined): string => {
  if (secs == null || !isFinite(secs) || secs <= 0) return "";
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

/** Axis tick: elapsed ms → "0:00" style minutes, or "1h 05m". */
export const fmtElapsed = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}:${String(s % 60).padStart(2, "0")}`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

const plural = (n: number, one: string, many: string) => `${fmtCount(n)} ${n === 1 ? one : many}`;

// ---- job predicates --------------------------------------------------------------------------------

const isTerminal = (j: TimelineJob) => j.phase === "done" || j.phase === "error" || j.phase === "cancelled";
const isRunning = (j: TimelineJob) => !isTerminal(j);
/** Content still uploading (a "scanning" job has finished its uploads). */
const contentRunning = (j: TimelineJob) =>
  (j.phase === "presigning" || j.phase === "uploading") && j.contentDoneAt == null;
const contentEnd = (j: TimelineJob) => j.contentDoneAt ?? j.finishedAt;
const scanTracked = (j: TimelineJob) => (j.scanTotal ?? 0) > 0;
const scanRunning = (j: TimelineJob) => scanTracked(j) && isRunning(j) && (j.scanCompleted ?? 0) < (j.scanTotal ?? 0);

const minOf = (xs: Array<number | undefined>) => {
  const v = xs.filter((x): x is number => typeof x === "number");
  return v.length ? Math.min(...v) : undefined;
};
const maxOf = (xs: Array<number | undefined>) => {
  const v = xs.filter((x): x is number => typeof x === "number");
  return v.length ? Math.max(...v) : undefined;
};

// ---- upload lanes (previews / originals / plain) -------------------------------------------------

interface LaneAgg {
  total: number;
  done: number;
  failed: number;
  bytesLeft: number;
  rate: number;
  running: boolean;
  paused: boolean;
  signedOut: boolean;
  preparing: boolean;
  cancelled: boolean;
  start?: number;
  end?: number;
}

const aggregate = (js: TimelineJob[]): LaneAgg => {
  let total = 0, done = 0, failed = 0, bytesLeft = 0, rate = 0;
  const live = js.filter(contentRunning);
  for (const j of js) {
    total += j.total;
    done += j.completed;
    failed += j.failed;
    if (contentRunning(j)) {
      bytesLeft += Math.max(0, j.bytesTotal - j.bytesSent);
      if (!j.paused && j.conc?.bytesPerSec) rate += j.conc.bytesPerSec;
    }
  }
  const running = live.length > 0;
  return {
    total, done, failed, bytesLeft, rate, running,
    paused: running && live.every((j) => j.paused),
    signedOut: live.some((j) => j.waitingForSignIn),
    preparing: running && live.every((j) => j.phase === "presigning"),
    // Cancelled before every file went (a pass cancelled after its uploads drained — e.g. while still
    // scanning — still counts as uploaded).
    cancelled: js.some((j) => j.phase === "cancelled" && j.completed + j.failed < j.total),
    start: minOf(js.map((j) => j.startedAt)),
    end: running ? undefined : maxOf(js.map(contentEnd)),
  };
};

const pctOf = (done: number, total: number) => (total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null);

const uploadLane = (
  key: "previews" | "originals" | "upload",
  label: string,
  short: string,
  js: TimelineJob[],
): TimelineStage => {
  const a = aggregate(js);
  const settled = a.done + a.failed;
  const etaSec = a.running && !a.paused && a.rate > 0 && a.bytesLeft > 0 ? a.bytesLeft / a.rate : null;
  let state: StageState;
  let stateLabel: string;
  let note: string | undefined;
  if (a.running) {
    if (a.signedOut) {
      state = "waiting";
      stateLabel = "Signed out";
      note = "Sign in to continue — nothing is lost";
    } else if (a.paused) {
      state = "paused";
      stateLabel = "Paused";
      note = "Resume to carry on where it stopped";
    } else {
      state = "active";
      stateLabel = a.preparing ? "Getting ready" : "Uploading";
    }
  } else if (a.cancelled) {
    state = "cancelled";
    stateLabel = "Cancelled";
    note = `Stopped at ${fmtCount(a.done)} of ${fmtCount(a.total)}`;
  } else if (a.done === 0 && a.failed > 0) {
    state = "failed";
    stateLabel = "Didn't upload";
    note = "Use Retry failed to try again";
  } else {
    state = "done";
    stateLabel = a.failed > 0 ? `Done · ${fmtCount(a.failed)} failed` : "Done";
    if (a.failed > 0) note = `${plural(a.failed, "photo", "photos")} didn't upload — use Retry failed`;
  }
  const pct = pctOf(settled, a.total);
  return {
    key,
    label,
    short,
    state,
    stateLabel,
    milestone: false,
    done: a.done,
    total: a.total,
    failed: a.failed,
    pct,
    start: a.start,
    end: a.end,
    rate: a.running && !a.paused && a.rate > 0 ? a.rate : undefined,
    rateUnit: "bytes",
    etaSec,
    summary: `${fmtCount(a.done)}/${fmtCount(a.total)}${pct != null && state !== "done" ? ` · ${pct}%` : ""}`,
    note,
  };
};

// ---- scan lane -------------------------------------------------------------------------------------

const scanLabel = (copies: TimelineJob[]) => {
  const ctx = copies.find((j) => j.scan)?.scan;
  if (ctx && ctx.needsFaces === false && ctx.needsPeople) return "Finding people & bib numbers";
  if (ctx && !ctx.needsPeople && ctx.needsFaces !== false) return "Finding faces";
  return "Finding faces & people";
};

const scanLane = (copies: TimelineJob[], now: number): TimelineStage => {
  const label = scanLabel(copies);
  const tracked = copies.filter(scanTracked);
  const base = { key: "scan" as const, label, short: "Faces", milestone: false, failed: 0 };
  if (!tracked.length) {
    // Not run in the browser for this folder: the server finds faces & people as each photo lands,
    // so there's nothing here to wait for.
    return {
      ...base,
      state: "skipped",
      stateLabel: "Automatic",
      done: 0,
      total: 0,
      pct: null,
      etaSec: null,
      summary: "",
      note: "Done on our servers as each photo arrives",
    };
  }
  let total = 0, done = 0;
  for (const j of tracked) { total += j.scanTotal ?? 0; done += j.scanCompleted ?? 0; }
  const live = tracked.filter(scanRunning);
  const running = live.length > 0;
  const start = minOf(tracked.map((j) => j.uploadingAt ?? j.startedAt));
  const end = running ? undefined : maxOf(tracked.map((j) => j.finishedAt));
  const paused = running && live.every((j) => j.paused);
  const cancelled = !running && tracked.some((j) => j.phase === "cancelled") && done < total;
  // Average pace since scanning began — on-device scanning is steady CPU work, so this is a fair ETA.
  const elapsed = start != null ? (now - start) / 1000 : 0;
  const perSec = running && !paused && done > 0 && elapsed >= 5 ? done / elapsed : 0;
  const left = Math.max(0, total - done);
  const etaSec = perSec > 0 && left > 0 ? left / perSec : null;
  const state: StageState = running ? (paused ? "paused" : "active") : cancelled ? "cancelled" : "done";
  const stateLabel =
    state === "active" ? "Scanning" : state === "paused" ? "Paused" : state === "cancelled" ? "Cancelled" : "Done";
  const pct = pctOf(done, total);
  return {
    ...base,
    state,
    stateLabel,
    done,
    total,
    pct,
    start,
    end,
    rate: perSec > 0 ? perSec * 60 : undefined,
    rateUnit: "photos",
    etaSec,
    summary: `${fmtCount(done)}/${fmtCount(total)}${pct != null && state !== "done" ? ` · ${pct}%` : ""}`,
    note: cancelled ? "Photos already up are scanned on our servers" : undefined,
  };
};

// ---- milestones ------------------------------------------------------------------------------------

const milestone = (
  key: "live" | "complete",
  label: string,
  short: string,
  state: StageState,
  stateLabel: string,
  at: number | undefined,
  note?: string,
): TimelineStage => ({
  key, label, short, state, stateLabel, milestone: true, done: 0, total: 0, failed: 0, pct: null, at,
  etaSec: null, summary: "", note,
});

/** How long after the previews finish the originals pass may take to appear (it first asks the server
 *  which photos sold) before the timeline stops saying "Starting". */
const ORIGINALS_HANDOFF_MS = 60_000;

const projectedEnd = (s: TimelineStage, now: number) =>
  s.end ?? (s.etaSec != null ? now + s.etaSec * 1000 : undefined);

// ---- overlap ---------------------------------------------------------------------------------------

const markParallel = (stages: TimelineStage[], now: number) => {
  const spans = stages.filter((s) => !s.milestone && s.start != null && s.state !== "skipped");
  for (const a of spans) {
    const with_: StageKey[] = [];
    for (const b of spans) {
      if (a === b) continue;
      const lo = Math.max(a.start!, b.start!);
      const hi = Math.min(a.end ?? now, b.end ?? now);
      if (hi - lo > 1000) with_.push(b.key);
    }
    if (with_.length) a.parallelWith = with_;
  }
};

// ---- pipelines -------------------------------------------------------------------------------------

/** Pipeline a job belongs to: a preview-first copy pass and its originals share the copy job's id. */
export const pipelineKey = (j: Pick<UploadJob, "id" | "pairId">) => j.pairId ?? j.id;

/** Group jobs into pipelines, keeping first-seen order. */
export const groupPipelines = <J extends TimelineJob>(jobs: readonly J[]): J[][] => {
  const m = new Map<string, J[]>();
  for (const j of jobs) {
    const k = pipelineKey(j);
    const g = m.get(k);
    if (g) g.push(j);
    else m.set(k, [j]);
  }
  return [...m.values()];
};

/** Sold photos whose originals are still to go: the unsent front of a main pass that leads with sold
 *  photos, plus whatever's left in prioritised sold batches. */
const soldStillWaiting = (originals: TimelineJob[]) => {
  let n = 0;
  for (const j of originals) {
    if (!j.soldFirst || !isRunning(j)) continue;
    if (j.seq < 0) n += Math.max(0, j.total - j.completed - j.failed);
    else if (j.files) {
      for (let i = 0; i < Math.min(j.soldFirst, j.files.length); i += 1) {
        const s = j.files[i].status;
        if (s === "queued" || s === "uploading") n += 1;
      }
    } else n += Math.max(0, j.soldFirst - j.completed - j.failed);
  }
  return n;
};

/**
 * One pipeline's jobs → its timeline. `jobs` must all belong to the same pipeline (see groupPipelines).
 */
export const buildTimeline = (jobs: readonly TimelineJob[], opts: TimelineOptions): UploadTimeline | null => {
  if (!jobs.length) return null;
  const { now } = opts;
  const copies = jobs.filter((j) => j.previewFirst === "copy");
  const originals = jobs.filter((j) => j.previewFirst === "original");
  const plain = jobs.filter((j) => !j.previewFirst);
  const kind: UploadTimeline["kind"] = copies.length ? "preview-first" : originals.length ? "originals" : "plain";
  const first = jobs.reduce((a, b) => (b.startedAt < a.startedAt ? b : a));
  const startedAt = first.startedAt;
  const stages: TimelineStage[] = [];

  const running = jobs.some(isRunning);
  const runningJobs = jobs.filter(isRunning);
  const paused = runningJobs.length > 0 && runningJobs.every((j) => j.paused);
  const signedOut = runningJobs.some((j) => j.waitingForSignIn);
  const cancelled = !running && jobs.some((j) => j.phase === "cancelled");

  let headline = "";
  let reason: string | undefined;
  let complete = false;

  if (kind === "plain") {
    const lane = uploadLane("upload", plain.some((j) => j.kind === "video") ? "Uploading videos" : "Uploading photos", "Upload", plain);
    stages.push(lane);
    complete = lane.state === "done" && lane.failed === 0;
    headline =
      lane.state === "active" ? `Uploading ${fmtCount(lane.done)} of ${fmtCount(lane.total)}`
      : lane.state === "done" ? (lane.failed ? `Finished — ${plural(lane.failed, "file", "files")} didn't upload` : "Upload complete")
      : lane.state === "failed" ? "Upload didn't go through"
      : lane.state === "cancelled" ? "Cancelled"
      : lane.stateLabel;
  } else {
    // --- Previews
    const prev = copies.length ? uploadLane("previews", "Previews uploading", "Previews", copies) : null;
    if (prev) stages.push(prev);
    const prevSettled = !!prev && prev.state !== "active" && prev.state !== "paused" && prev.state !== "waiting";

    // --- Live on previews (milestone)
    if (prev) {
      if (prevSettled && prev.done > 0) {
        if (opts.published === false) {
          stages.push(milestone("live", "Live on previews", "Live", "waiting", "Not published", prev.end,
            opts.autoPublishArmed ? "Publishing now" : "Previews are up — publish the gallery so buyers can see it"));
        } else {
          stages.push(milestone("live", "Live on previews", "Live", "done", "Live", prev.end, "Buyers can browse and buy from the previews"));
        }
      } else if (prevSettled) {
        stages.push(milestone("live", "Live on previews", "Live", "skipped", "Not reached", undefined, "No previews made it up"));
      } else {
        stages.push(milestone("live", "Live on previews", "Live", "pending", "Next", projectedEnd(prev, now),
          opts.published ? "Photos appear in the gallery as they land"
          : opts.autoPublishArmed ? "Goes live as soon as the previews are up"
          : opts.published === false ? "Publish the gallery to let buyers in"
          : "Buyers can browse once the previews are up"));
      }
    }

    // --- Scanning
    const scan = copies.length ? scanLane(copies, now) : null;

    // --- Originals
    const laterBy = originals.length ? undefined : maxOf(copies.map((j) => j.originalsBy));
    let orig: TimelineStage;
    if (originals.length) {
      orig = uploadLane("originals", "Originals uploading", "Originals", originals);
      const priority: PrioritySegment[] = [];
      for (const j of originals) {
        if (!j.soldFirst) continue;
        if (j.seq < 0) priority.push({ kind: "batch", start: j.startedAt, end: isRunning(j) ? undefined : contentEnd(j), count: j.total, done: j.completed });
        else priority.push({ kind: "lead", start: j.startedAt, count: j.soldFirst, done: Math.min(j.soldFirst, j.completed) });
      }
      if (priority.length) orig.priority = priority.sort((a, b) => a.start - b.start);
      orig.label = priority.length ? "Originals uploading (sold first)" : "Originals uploading";
    } else if (laterBy) {
      // "Upload previews now, originals later": no originals pass here — they're promised for later.
      orig = {
        key: "originals", label: "Originals later", short: "Originals", state: "pending", stateLabel: "Later",
        milestone: false, done: 0, total: prev?.total ?? 0, failed: 0, pct: null, etaSec: null, summary: "", at: laterBy,
        note: `Expected by ${formatOriginalsBy(laterBy)} — add the same folder later, from any device, to upload them`,
      };
    } else if (prev && !prevSettled) {
      orig = {
        key: "originals", label: "Originals uploading", short: "Originals", state: "waiting", stateLabel: "Waiting",
        milestone: false, done: 0, total: 0, failed: 0, pct: null, etaSec: null, summary: "",
        note: prev.state === "paused" ? "Waiting — the previews are paused" : "Waiting for the previews to finish uploading",
      };
    } else if (prev && prev.done > 0 && prev.state !== "cancelled" && now - (prev.end ?? now) < ORIGINALS_HANDOFF_MS) {
      orig = {
        key: "originals", label: "Originals uploading", short: "Originals", state: "waiting", stateLabel: "Starting",
        milestone: false, done: 0, total: 0, failed: 0, pct: null, etaSec: null, summary: "",
        note: "Getting the originals ready — sold photos go first",
      };
    } else {
      orig = {
        key: "originals", label: "Originals uploading", short: "Originals", state: "skipped", stateLabel: "Not sent",
        milestone: false, done: 0, total: 0, failed: 0, pct: null, etaSec: null, summary: "",
        note: prev?.state === "cancelled" ? "Not sent — the upload was cancelled. Add the folder again to finish"
          : prev && prev.done > 0 ? "Not in this queue — if any are still owed, add the folder again"
          : "Not sent — no previews made it up",
      };
    }

    // Notes that depend on the other lanes.
    const soldWaitingNow = soldStillWaiting(originals);
    if (scan && (scan.state === "active" || scan.state === "paused")) {
      const left = Math.max(0, scan.total - scan.done);
      scan.note = orig.state === "active" || orig.state === "done" || orig.state === "waiting"
        ? `${fmtCount(left)} left — doesn't hold up the originals`
        : `${fmtCount(left)} left`;
      if (prev && prev.state === "active") scan.note = "Runs alongside the upload";
    }
    if (orig.state === "active" && !orig.note) {
      orig.note = soldWaitingNow > 0
        ? `${plural(soldWaitingNow, "sold photo", "sold photos")} going first`
        : scan && scan.state === "active" ? "Runs alongside the face & people search" : undefined;
    }

    if (scan) stages.push(scan);
    stages.push(orig);

    // --- Complete (milestone)
    const scanBusy = !!scan && (scan.state === "active" || scan.state === "paused");
    if (orig.state === "done" && !scanBusy) {
      const at = maxOf([orig.end, scan?.end]);
      if (orig.failed > 0) {
        stages.push(milestone("complete", "Complete", "Done", "failed", "Not quite",
          at, `${plural(orig.failed, "original", "originals")} didn't upload — use Retry failed`));
      } else {
        complete = true;
        stages.push(milestone("complete", "Complete", "Done", "done", "Complete", at, "Originals delivered — orders waiting on them are released"));
      }
    } else if (orig.state === "done" && scanBusy) {
      stages.push(milestone("complete", "Complete", "Done", "pending", "Almost", projectedEnd(scan!, now), "Originals delivered — finishing the face & people search"));
    } else if (orig.state === "cancelled" || orig.state === "skipped" || (cancelled && orig.state !== "done")) {
      stages.push(milestone("complete", "Complete", "Done", "cancelled", "Not reached", undefined, "Stopped before every original was up"));
    } else if (orig.state === "failed") {
      stages.push(milestone("complete", "Complete", "Done", "failed", "Not reached", undefined, "The originals didn't upload — use Retry failed"));
    } else if (laterBy) {
      stages.push(milestone("complete", "Complete", "Done", "pending", "Later", laterBy,
        "When the originals are up, orders waiting on them are delivered"));
    } else {
      stages.push(milestone("complete", "Complete", "Done", "pending", "Later", projectedEnd(orig, now),
        "When every original is up, orders waiting on them are released"));
    }

    // --- Headline + the one reason that matters now
    const liveNow = opts.published !== false;
    if (prev && (prev.state === "active" || prev.state === "paused" || prev.state === "waiting")) {
      headline = `Uploading previews — ${fmtCount(prev.done)} of ${fmtCount(prev.total)}`;
      reason = laterBy
        ? `Originals later — expected by ${formatOriginalsBy(laterBy)}`
        : "Originals start as soon as the previews finish uploading";
    } else if (laterBy && prev && prev.done > 0 && prev.state !== "cancelled") {
      headline = originalsLaterStatus(laterBy);
      reason = "Add the same folder later, from any device, to upload the originals";
    } else if (orig.state === "waiting" && orig.stateLabel === "Starting") {
      headline = liveNow ? "Live on previews · getting the originals ready" : "Previews are up · getting the originals ready";
    } else if (orig.state === "active" || orig.state === "paused" || (orig.state === "waiting" && originals.length)) {
      const count = `${fmtCount(orig.done)} of ${fmtCount(orig.total)}`;
      headline = kind === "preview-first"
        ? `${liveNow ? "Live on previews" : "Previews are up"} · originals uploading ${count}`
        : `Uploading originals — ${count}`;
      reason = soldWaitingNow > 0
        ? `${plural(soldWaitingNow, "sold photo is", "sold photos are")} going first`
        : scanBusy ? `Scanning ${fmtCount(scan!.total - scan!.done)} left — doesn't hold up the originals` : undefined;
    } else if (orig.state === "done" && scanBusy) {
      headline = "Originals delivered · finishing the face & people search";
    } else if (complete) {
      headline = "Complete — every original is up";
    } else if (cancelled) {
      headline = "Cancelled";
      reason = orig.state === "skipped" || orig.state === "cancelled"
        ? "Originals weren't all sent — add the folder again to finish"
        : undefined;
    } else {
      headline = stages[stages.length - 1].note ?? "Finished";
    }
    if (kind === "preview-first" && opts.published === false && prevSettled && prev && prev.done > 0 && !opts.autoPublishArmed && !reason) {
      reason = "Previews are up — publish the gallery so buyers can see it";
    }
  }

  // Overrides: the run is on hold.
  if (signedOut) {
    headline = "Signed out — sign in to continue";
    reason = "Uploads are on hold until you sign in again. Nothing is lost.";
  } else if (paused) {
    headline = "Paused";
    reason = "Resume to carry on from where it stopped";
  }

  markParallel(stages, now);

  // Time axis: from the first job's start to now (or the last end), plus room for projected ends —
  // capped so one wild ETA can't squash everything that already happened against the left edge.
  const elapsed = Math.max(1000, now - startedAt);
  let axisEnd: number;
  if (running) {
    const proj = maxOf(stages.map((s) => (s.milestone ? s.at : projectedEnd(s, now))));
    const cap = now + elapsed * 2;
    axisEnd = Math.max(now + elapsed * 0.06, Math.min(proj ?? now, cap));
  } else {
    axisEnd = Math.max(startedAt + 1000, maxOf(stages.flatMap((s) => [s.end, s.at])) ?? maxOf(jobs.map((j) => j.finishedAt)) ?? now);
  }

  return {
    id: pipelineKey(first),
    kind,
    galleryName: first.galleryName,
    engineGuid: first.engineGuid,
    folder: first.folder,
    startedAt,
    now,
    axisEnd,
    running,
    paused,
    signedOut,
    cancelled,
    complete,
    headline,
    reason,
    stages,
    soldWaiting: soldStillWaiting(originals),
    soldFirst: originals.reduce((n, j) => n + (j.soldFirst ?? 0), 0),
    failReasons: mergeCounts(jobs.map((j) => j.failReasons)),
    alreadyThere: jobs.reduce((n, j) => n + (j.alreadyThere ?? 0), 0),
    retrying: retryingOf(runningJobs, now),
  };
};

/** The automatic retries pending across running jobs: how many files, and the soonest start. */
const retryingOf = (running: readonly TimelineJob[], now: number): UploadTimeline["retrying"] => {
  let count = 0;
  let at: number | undefined;
  for (const j of running) {
    if (!j.retrying?.count) continue;
    count += j.retrying.count;
    if (j.retrying.at != null) at = at == null ? j.retrying.at : Math.min(at, j.retrying.at);
  }
  if (!count) return undefined;
  return { count, inSec: at == null ? 0 : Math.max(0, Math.round((at - now) / 1000)) };
};

/** Every pipeline in `jobs`, newest first. */
export const buildTimelines = (jobs: readonly TimelineJob[], opts: TimelineOptions): UploadTimeline[] =>
  groupPipelines(jobs)
    .map((g) => buildTimeline(g, opts))
    .filter((t): t is UploadTimeline => !!t)
    .sort((a, b) => b.startedAt - a.startedAt);

/** The timeline to feature: a running one in `folder`, else any running one, else the newest. */
export const pickTimeline = (timelines: readonly UploadTimeline[], folder?: string): UploadTimeline | null =>
  timelines.find((t) => t.running && folder != null && t.folder === folder) ??
  timelines.find((t) => t.running) ??
  timelines[0] ??
  null;

/** Position (0–100%) of `t` on a timeline's axis. */
export const axisPct = (tl: Pick<UploadTimeline, "startedAt" | "axisEnd">, t: number) =>
  Math.max(0, Math.min(100, ((t - tl.startedAt) / Math.max(1, tl.axisEnd - tl.startedAt)) * 100));
